import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';


export default function VerifyEmail() {
  const { currentUser, resendVerificationEmail, logout } = useAuth();
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isLoadingResend, setIsLoadingResend] = useState(false);
  const [isLoadingCheck, setIsLoadingCheck] = useState(false);
  const [lastResendTime, setLastResendTime] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslation();
  
  const from = location.state?.from?.pathname || '/';
  
  // Timer reference for automatic checking
  const checkIntervalRef = useRef(null);
  
  // Set up timer for resend cooldown
  useEffect(() => {
    let timerId;
    if (timeLeft > 0) {
      timerId = setTimeout(() => {
        setTimeLeft(timeLeft - 1);
      }, 1000);
    }
    return () => clearTimeout(timerId);
  }, [timeLeft]);
  
  // Check immediately and periodically if email is verified
  useEffect(() => {
    if (!currentUser) {
      navigate('/auth');
      return;
    }
    
    const checkVerification = async () => {
        // Reload user data from Firebase Auth
        try {
            await currentUser.reload();
            // Check verification status AFTER reloading
            if (currentUser.emailVerified) {
                console.log('Email verified, navigating to main app.');
                navigate(from, { replace: true });
                return true; // Indicate verification is complete
            }
        } catch (error) {
            console.error('Error reloading user during periodic check:', error);
            // Don't stop checking unless it's a specific auth error?
            if (error.code?.startsWith('auth/')) {
                setMessage({type: 'error', text: 'Session error. Please log out and log in again.'});
                clearInterval(checkIntervalRef.current);
            }
        }
        return false; // Not verified yet
    };

    // Initial check
    checkVerification();

    // Set up interval to automatically check every 5 seconds
    checkIntervalRef.current = setInterval(async () => {
      console.log('Checking email verification status...');
      const verified = await checkVerification();
      if (verified) {
          clearInterval(checkIntervalRef.current); // Stop checking once verified
      }
    }, 5000); // Check every 5 seconds

    // Clean up interval on component unmount
    return () => clearInterval(checkIntervalRef.current);
    
  }, [currentUser, navigate, from]); // Rerun if currentUser changes
  
  // Handle resend verification email
  const handleResendVerification = async () => {
    // Check if we're in the cooldown period
    const now = Date.now();
    if (now - lastResendTime < 60000) { // 1 minute cooldown
      setMessage({
        type: 'warning',
        text: t('auth.resendCooldown')
      });
      return;
    }
    
    try {
      setIsLoadingResend(true);
      setMessage({ type: '', text: '' });
      await resendVerificationEmail();
      setMessage({
        type: 'success',
        text: t('auth.verificationEmailResent')
      });
      
      // Set cooldown period
      setLastResendTime(now);
      setTimeLeft(60); // 60 second cooldown
    } catch (error) {
      console.error('Failed to resend verification email:', error);
      setMessage({
        type: 'error',
        text: t('auth.resendVerificationError')
      });
    } finally {
      setIsLoadingResend(false);
    }
  };
  
  // Manual check (kept for user reassurance, though auto-check should handle it)
  const handleManualCheckVerification = () => {
    if (currentUser) {
      setIsLoadingCheck(true);
      currentUser.reload().then(() => {
        if (currentUser.emailVerified) {
          setMessage({
            type: 'success',
            text: t('auth.emailVerified')
          });
          // Redirect after a short delay (auto-check might have already redirected)
          setTimeout(() => {
            navigate(from, { replace: true });
          }, 1500);
        } else {
          setMessage({
            type: 'warning',
            text: t('auth.emailNotVerified')
          });
        }
        setIsLoadingCheck(false);
      }).catch(error => {
        console.error('Error refreshing user status:', error);
        setMessage({
          type: 'error',
          text: t('errors.general')
        });
        setIsLoadingCheck(false);
      });
    }
  };
  
  // Handle logout
  const handleLogout = async () => {
    clearInterval(checkIntervalRef.current); // Stop checking on logout
    try {
      await logout();
      navigate('/auth', { replace: true });
    } catch (error) {
      console.error('Failed to log out:', error);
      setMessage({
        type: 'error',
        text: t('errors.general')
      });
    }
  };
  
  // Render nothing if currentUser is not yet available (avoids flash)
  if (!currentUser) {
    return null;
  }
  
  // If somehow the user is already verified when this mounts, allow effect to redirect
  if (currentUser.emailVerified) {
      return null;
  }
  
  return (
    <div className="flex items-center justify-center min-h-screen p-5 bg-gray-100 dark:bg-gray-900">
      <div className="w-full max-w-lg p-8 text-center bg-white rounded-lg shadow-md dark:bg-gray-800">
        <h1 className="mb-4 text-2xl font-bold text-gray-800 dark:text-gray-100">
          {t('auth.verifyYourEmail')}
        </h1>
        <p className="mb-6 text-gray-600 dark:text-gray-300 leading-relaxed">
          {t('auth.verificationInstructions')}
        </p>
        
        <div className="p-3 mb-6 font-medium break-all rounded-md bg-gray-100 dark:bg-gray-900 dark:text-gray-100">
          <strong>{currentUser.email}</strong>
        </div>
        
        {message.text && (
          <div className={`p-3 mb-5 text-sm rounded-md ${
            message.type === 'success' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200 border border-green-200 dark:border-green-800'
              : message.type === 'warning'
                ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-800'
                : message.type === 'error'
                  ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200 border border-red-200 dark:border-red-800'
                  : ''
          }`}>
            {message.text}
          </div>
        )}
        
        <div className="flex flex-col gap-3 mt-6">
          <button 
            className="py-3 px-4 font-medium text-white bg-blue-500 hover:bg-blue-600 rounded-md transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-70 disabled:cursor-not-allowed dark:bg-blue-600 dark:hover:bg-blue-700"
            onClick={handleManualCheckVerification}
            disabled={isLoadingCheck}
          >
            {isLoadingCheck ? t('common.loading') : t('auth.iAlreadyVerified')}
          </button>
          
          <button 
            className="py-3 px-4 font-medium text-white bg-gray-500 hover:bg-gray-600 rounded-md transition focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50 disabled:opacity-70 disabled:cursor-not-allowed dark:bg-gray-600 dark:hover:bg-gray-700"
            onClick={handleResendVerification}
            disabled={isLoadingResend || timeLeft > 0}
          >
            {isLoadingResend 
              ? t('common.loading') 
              : timeLeft > 0 
                ? `${t('auth.resendIn')} ${timeLeft}s` 
                : t('auth.resendVerification')}
          </button>
          
          <button 
            className="py-3 px-4 font-medium text-red-600 bg-transparent border border-red-600 rounded-md hover:bg-red-50 dark:text-red-400 dark:border-red-400 dark:hover:bg-red-900/20 transition focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 disabled:opacity-70 disabled:cursor-not-allowed"
            onClick={handleLogout}
            disabled={isLoadingResend || isLoadingCheck}
          >
            {t('auth.logout')}
          </button>
        </div>
      </div>
    </div>
  );
} 